import { Component, inject } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSearchPanelComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import {
    AccessType,
    AuthService,
    CurrencyCode,
    InvoiceGridViewDto,
    InvoiceStatus,
    PermissionType,
} from '@services';
import EnumUtils from '../../../shared/enum-utils';
import { CommonModule, NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { Router, RouterLink } from '@angular/router';
import { DATE_FORMAT } from 'src/app/shared/constants';
import { InvoiceStatusPipe } from 'src/app/pipes/invoice-status.pipe';

@Component({
    selector: 'app-invoices',
    imports: [
        CommonModule,
        DxDataGridComponent,
        DxoScrollingComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoLoadPanelComponent,
        DxoPositionComponent,
        DxoSortingComponent,
        DxoSearchComponent,
        DxoHeaderFilterComponent,
        DxoFilterRowComponent,
        DxoColumnChooserComponent,
        DxoSearchPanelComponent,
        DxoSelectionComponent,
        DxoExportComponent,
        DxoToolbarComponent,
        DxiItemComponent,
        DxButtonComponent,
        DxiColumnComponent,
        DxTemplateDirective,
        NgClass,
        FilterPanelComponent,
        DxFormComponent,
        RouterLink,
        InvoiceStatusPipe,
    ],
    providers: [UpperCasePipe, InvoiceStatusPipe],
    templateUrl: './invoices.component.html',
})
export class InvoicesComponent extends GridPageComponent<InvoiceGridViewDto> {
    private router = inject(Router);
    private authService = inject(AuthService);
    private upperCasePipe = inject(UpperCasePipe);
    private invoiceStatusPipe = inject(InvoiceStatusPipe);

    protected override exportName: string = 'Invoice Report';
    protected offset: number = 100;
    public dateFormat = DATE_FORMAT;

    dataSource = this.dataSources.invoices();

    statusOptions = EnumUtils.getEnumOptions(InvoiceStatus, (v) =>
        this.invoiceStatusPipe.transform(v),
    );

    currencyOptions = EnumUtils.getEnumOptions(CurrencyCode, (v) =>
        this.upperCasePipe.transform(v),
    );

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'poNumber', op: 'contains' },
        { field: 'serviceType', op: 'contains' },
        { field: 'vendor', op: 'contains' },
        { field: 'issueDate', op: '>=' },
        { field: 'dueDate', op: '>=' },
        { field: 'totalAmountAfterTax', op: '>=' },
        { field: 'subTotal', op: '>=' },
        { field: 'taxAmount', op: '>=' },
        { field: 'totalAmount', op: '>=' },
        { field: 'convertedSubTotal', op: '>=' },
        { field: 'convertedTaxAmount', op: '>=' },
        { field: 'convertedTotalAmount', op: '>=' },
        { field: 'status', op: '=' },
    ];

    constructor() {
        super();
        this.init(InvoiceGridViewDto);
    }

    protected getBadgeClass(status: InvoiceStatus | undefined): string {
        return EnumUtils.getInvoiceStatusBadgeClass(status);
    }

    addInvoice = async () => {
        await this.router.navigate(['/invoicing/invoices/create']);
    };

    async onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        await this.router.navigate([
            `/invoicing/invoices/details/${e.data.number}`,
        ]);
    }

    get canCreate(){
        return this.authService.checkPermission(PermissionType.Invoices, AccessType.Edit);
    }
}